import { Helmet } from "react-helmet-async";
import { useSeoContext } from "../context/SeoContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function siteBase() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return BACKEND_URL.replace(/\/$/, "");
}

function absUrl(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  if (maybeRelative.startsWith("/")) return `${siteBase()}${maybeRelative}`;
  return maybeRelative;
}

function formatTitle(template, value) {
  if (!template || !value) return value || template || "";
  if (template.includes("%s")) return template.replace("%s", value);
  return `${value} ${template}`.trim();
}

/**
 * Universal SEO component. Pass either:
 *  - `pageSlug` (e.g. "home", "shop") to read the per-static-page override, OR
 *  - `seo` (a SeoBlock-shaped object from a product/article), OR
 *  - explicit `title`, `description`, `image`, `canonical`, `noindex`, `keywords` props
 *
 * Anything missing falls back to the global defaults loaded by SeoProvider.
 * Additional `jsonLd` (object or array of objects) is rendered as JSON-LD <script>.
 */
export default function SEO({
  pageSlug,
  seo,
  title,
  description,
  image,
  canonical,
  noindex,
  keywords,
  path,
  ogType = "website",
  jsonLd,
}) {
  const { global, byPage } = useSeoContext();
  const pageOverride = (pageSlug && byPage?.[pageSlug]) || {};
  const entityOverride = seo || {};

  const finalTitleValue =
    title ||
    entityOverride.title ||
    pageOverride.title ||
    global?.default_title ||
    "";
  const finalTitle = formatTitle(global?.title_template, finalTitleValue);

  const finalDescription =
    description ||
    entityOverride.description ||
    pageOverride.description ||
    global?.default_description ||
    "";

  const finalImage = absUrl(
    image ||
    entityOverride.image ||
    pageOverride.image ||
    global?.default_image ||
    ""
  );

  const computedCanonical = canonical ||
    entityOverride.canonical ||
    pageOverride.canonical ||
    (path ? `${siteBase()}${path}` : (typeof window !== "undefined" ? window.location.href : ""));

  const robotsValue = (noindex ?? entityOverride.noindex ?? pageOverride.noindex)
    ? "noindex,nofollow"
    : (global?.robots_default || "index,follow");

  const finalKeywords =
    keywords ||
    entityOverride.keywords ||
    pageOverride.keywords ||
    global?.default_keywords ||
    "";

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      <html lang={(global?.locale || "fr_FR").slice(0, 2)} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords ? <meta name="keywords" content={finalKeywords} /> : null}
      <meta name="robots" content={robotsValue} />
      {computedCanonical ? <link rel="canonical" href={computedCanonical} /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      {finalImage ? <meta property="og:image" content={finalImage} /> : null}
      {computedCanonical ? <meta property="og:url" content={computedCanonical} /> : null}
      <meta property="og:site_name" content={global?.site_name || "Herbalife Shop"} />
      <meta property="og:locale" content={global?.locale || "fr_FR"} />

      {/* Twitter */}
      <meta name="twitter:card" content={finalImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {finalImage ? <meta name="twitter:image" content={finalImage} /> : null}
      {global?.twitter_handle ? <meta name="twitter:site" content={global.twitter_handle} /> : null}

      {jsonLdArray.map((obj, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// --- JSON-LD helpers ---
export function productJsonLd({ product, urlPath, currency = "EUR" }) {
  if (!product) return null;
  const url = `${siteBase()}${urlPath}`;
  const img = absUrl(product.image_url || (product.images || [])[0]);
  const inStock = (product.stock ?? 0) > 0;
  const price = product.is_promo && product.promo_price ? product.promo_price : product.price;
  const obj = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    description: (product.description || "").replace(/<[^>]+>/g, "").slice(0, 500),
    image: img || undefined,
    sku: product.id,
    category: product.category,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: currency,
      price: price,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  };
  if ((product.rating_count || 0) > 0 && product.rating_avg) {
    obj.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(product.rating_avg).toFixed(1),
      reviewCount: product.rating_count,
    };
  }
  return obj;
}

export function articleJsonLd({ article, urlPath, authorName }) {
  if (!article) return null;
  return {
    "@context": "https://schema.org/",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: absUrl(article.cover_image),
    url: `${siteBase()}${urlPath}`,
    datePublished: article.created_at,
    author: { "@type": "Person", name: article.author || authorName || "Équipe Herbalife Shop" },
  };
}

export function organizationJsonLd({ global }) {
  if (!global) return null;
  return {
    "@context": "https://schema.org/",
    "@type": "Organization",
    name: global.site_name,
    url: global.organization_url || siteBase(),
    logo: absUrl(global.default_image),
  };
}

export function breadcrumbJsonLd(items) {
  if (!items?.length) return null;
  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${siteBase()}${it.path}`,
    })),
  };
}
